/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { GetStaticProps, NextLayoutComponentType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { Api } from '@/api';
import MainLayout from '@/layouts/main';
import AppTitle from '@/components/common/title';
import { routes } from '@/infrastructure/const/routes';
import AppContainer from '@/components/layout/container';
import { localeList } from '@/public/locales/locale-list';
import ReadSurahsList from '@/components/read-surahs/list';
import { IReciter } from '@/infrastructure/interfaces/i-reciter';
import { IReadSurah } from '@/infrastructure/interfaces/i-read-surah';
import { readSurahListFields } from '@/infrastructure/class/read-surah';
import RecitersFeaturedCarousel from '@/components/reciters/featured-carousel';
import { allRecordsPaginationOptions } from '@/infrastructure/const/pagination';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import { addDomainForReciterImages } from '@/utils/helpers/add-domain-for-reciter-images';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import {
  baseMarkupSchema,
  organizationMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';
import s from './styles.module.scss';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';

const api = Api.getInstance();
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;

interface IHomePageProps {
  reciters: IReciter[];
  readSurahs: IReadSurah[];
}

const HomePage: NextLayoutComponentType<{ data: string }> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<IHomePageProps>(data);

  const { reciters, readSurahs } = decrypted;

  const { t: tMeta } = useTranslation('meta');
  const { t: tCommon } = useTranslation('common');

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const router = useRouter();
  const { locale } = router;

  const canonicalURL = `${SITE_URL}${getLocaleForUrl(locale)}` + routes.home;

  const shema = baseMarkupSchema([
    webPageMarkupSchema(
      routes.home,
      tMeta('homePageTitle'),
      tMeta('homePageDescription')
    ),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
  ]);

  useEffect(() => {
    setBreadcrumbs([]);
  }, []);

  return (
    <>
      <Head>
        <title>{tMeta('homePageTitle')}</title>
        <meta name="description" content={tMeta('homePageDescription')} />
        <link rel="canonical" href={canonicalURL} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(shema) }}
        />
      </Head>
      <main>
        <AppContainer>
          <AppTitle title={tCommon('welcomeToQuranPro')} main />
          <RecitersFeaturedCarousel
            className={s['featured-carousel']}
            items={reciters}
          />
          <ReadSurahsList items={readSurahs} className={s['read-surah']} />
        </AppContainer>
      </main>
    </>
  );
};

HomePage.getLayout = (page) => {
  return <MainLayout>{page}</MainLayout>;
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  try {
    const { locale = '' } = ctx;

    const { data: recitersResult } = await api.getFeaturedReciters(
      allRecordsPaginationOptions,
      ['feature_cover', 'slug', 'title_en', 'featured_order']
    );
    const { results: reciters } = recitersResult;
    reciters.map((reciters) => {
      addDomainForReciterImages(reciters);
    });

    const { data: readSurahsResult } = await api.getReadSurahs(
      allRecordsPaginationOptions,
      readSurahListFields
    );
    const { results: readSurahs } = readSurahsResult;

    const data = {
      reciters,
      readSurahs,
    };

    const { encryptPageProps } = await import('@/utils/crypto/page-props-encrypt');
    const encrypted = encryptPageProps(data);

    return {
      props: {
        data: encrypted,
        ...(await serverSideTranslations(locale, localeList)),
      },
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default HomePage;
